import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { formatNumber } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { FC_TRANSFER_DISPLAY_FALLBACK } from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';

const FC_TRANSFER_DISCREPANCY_TYPE = Object.freeze({
    expirationDate: 'expiration_date_discrepancy',
    totalUnits: 'total_unit_discrepancy',
    unitsPerCase: 'units_per_case_discrepancy',
});

/**
 * @param {Record<string, unknown> | null | undefined} item
 * @returns {Record<string, Record<string, unknown>>}
 */
function getDiscrepanciesByType(item) {
    const discrepancies = Array.isArray(item?.discrepancies) ? item.discrepancies : [];
    /** @type {Record<string, Record<string, unknown>>} */
    const map = {};
    for (const raw of discrepancies) {
        if (raw && typeof raw === 'object') {
            const discrepancy = /** @type {Record<string, unknown>} */ (raw);
            const type = String(discrepancy.discrepancyType ?? '').trim();
            if (type) {
                map[type] = discrepancy;
            }
        }
    }
    return map;
}

/**
 * @param {Record<string, unknown>} item
 * @param {Record<string, Record<string, unknown>>} discrepancies
 * @returns {unknown}
 */
function getResolvedReceivedUnitsPerCaseRaw(item, discrepancies) {
    const fromDiscrepancy = discrepancies[FC_TRANSFER_DISCREPANCY_TYPE.unitsPerCase]?.receivedUnitsPerCase;
    if (fromDiscrepancy !== undefined && fromDiscrepancy !== null && fromDiscrepancy !== '') {
        return fromDiscrepancy;
    }
    return (
        item?.receivedUnitsPerCase ??
        item?.received_units_per_case ??
        item?.receivedItem?.receivedUnitsPerCase ??
        item?.effectiveUnitsPerCase ??
        item?.sourceUnitsPerCaseSnapshot ??
        item?.source_units_per_case_snapshot
    );
}

/**
 * @param {Record<string, unknown>} item
 * @param {Record<string, Record<string, unknown>>} discrepancies
 * @returns {unknown}
 */
function getResolvedReceivedExpiresOnRaw(item, discrepancies) {
    const fromDiscrepancy = discrepancies[FC_TRANSFER_DISCREPANCY_TYPE.expirationDate]?.receivedExpiresOn;
    if (fromDiscrepancy !== undefined && fromDiscrepancy !== null && fromDiscrepancy !== '') {
        return fromDiscrepancy;
    }
    return (
        item?.receivedExpiresOn ??
        item?.received_expires_on ??
        item?.receivedItem?.receivedExpiresOn ??
        item?.effectiveExpiresOn ??
        item?.sourceExpiresOnSnapshot ??
        item?.source_expires_on_snapshot
    );
}

/**
 * Shared discrepancy-aware received values used by table render and draft defaults.
 *
 * @param {Record<string, unknown> | null | undefined} item
 * @returns {{ expiresOnRaw: unknown, unitsPerCaseRaw: unknown, casesRaw: unknown, looseRaw: unknown }}
 */
export function resolveReceivedDisplayFields(item) {
    const normalized = item ?? {};
    const discrepancies = getDiscrepanciesByType(normalized);
    const unitsPerCaseRaw = getResolvedReceivedUnitsPerCaseRaw(normalized, discrepancies);
    const expiresOnRaw = getResolvedReceivedExpiresOnRaw(normalized, discrepancies);
    const receivedTotalUnitsRaw = discrepancies[FC_TRANSFER_DISCREPANCY_TYPE.totalUnits]?.receivedTotalUnits;
    const hasReceivedTotalUnits =
        receivedTotalUnitsRaw !== undefined && receivedTotalUnitsRaw !== null && receivedTotalUnitsRaw !== '';
    const resolvedUpc = Number(unitsPerCaseRaw);
    const totalUnits = hasReceivedTotalUnits ? Number(receivedTotalUnitsRaw) : Number.NaN;
    const upc = Math.trunc(resolvedUpc);
    if (Number.isFinite(upc) && upc > 0 && Number.isFinite(totalUnits) && totalUnits >= 0) {
        const total = Math.trunc(totalUnits);
        return {
            casesRaw: Math.floor(total / upc),
            expiresOnRaw,
            looseRaw: total % upc,
            unitsPerCaseRaw,
        };
    }
    return {
        casesRaw:
            normalized?.receivedNumCases ??
            normalized?.received_num_cases ??
            normalized?.receivedItem?.receivedNumCases,
        expiresOnRaw,
        looseRaw:
            normalized?.receivedNumLooseUnits ??
            normalized?.received_num_loose_units ??
            normalized?.receivedItem?.receivedNumLooseUnits,
        unitsPerCaseRaw,
    };
}

/**
 * Human-readable "{n} case(s) / {n} loose unit(s)" line for tables and summaries.
 *
 * @param {unknown} casesRaw
 * @param {unknown} looseRaw
 * @returns {string}
 */
export function formatCasesAndLooseSummary(casesRaw, looseRaw) {
    const c =
        casesRaw === undefined || casesRaw === null || casesRaw === ''
            ? FC_TRANSFER_DISPLAY_FALLBACK
            : formatNumber(casesRaw, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    const l =
        looseRaw === undefined || looseRaw === null || looseRaw === ''
            ? FC_TRANSFER_DISPLAY_FALLBACK
            : formatNumber(looseRaw, { maximumFractionDigits: 0, minimumFractionDigits: 0 });

    let caseNoun = 'cases';
    if (c !== FC_TRANSFER_DISPLAY_FALLBACK && Number(casesRaw) === 1) {
        caseNoun = 'case';
    }

    let looseNoun = 'loose units';
    if (l !== FC_TRANSFER_DISPLAY_FALLBACK && Number(looseRaw) === 1) {
        looseNoun = 'loose unit';
    }

    return `${c} ${caseNoun} / ${l} ${looseNoun}`;
}

/**
 * Expected shipped qty summary.
 * Uses explicit expected fields when present, then legacy/shipped keys.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatExpectedQtySummary(item) {
    const cases =
        item?.expectedNumCases ??
        item?.expected_num_cases ??
        item?.numCases ??
        item?.num_cases ??
        item?.shippedNumCases;
    const loose =
        item?.expectedNumLooseUnits ??
        item?.expected_num_loose_units ??
        item?.numLooseUnits ??
        item?.num_loose_units ??
        item?.shippedNumLooseUnits;
    return formatCasesAndLooseSummary(cases, loose);
}

/**
 * Received qty summary for display.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatReceivedQtySummary(item) {
    const { casesRaw, looseRaw } = resolveReceivedDisplayFields(item);
    return formatCasesAndLooseSummary(casesRaw, looseRaw);
}

/**
 * Expected BBD: canonical expected fields first, then outbound snapshot fallbacks until backend ships explicit keys.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatExpectedBbd(item) {
    const raw =
        item?.expectedExpiresOn ??
        item?.expected_expires_on ??
        item?.sourceExpiresOnSnapshot ??
        item?.source_expires_on_snapshot ??
        item?.effectiveExpiresOn;
    return formatPurchaseOrderDate(raw, FC_TRANSFER_DISPLAY_FALLBACK);
}

/**
 * Received BBD: persisted receipt fields first, then effective “current line” value (pre-finalize drafts).
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatReceivedBbd(item) {
    const { expiresOnRaw } = resolveReceivedDisplayFields(item);
    const raw = expiresOnRaw;
    return formatPurchaseOrderDate(raw, FC_TRANSFER_DISPLAY_FALLBACK);
}

/**
 * Expected count/case: canonical expected fields first, then snapshot fallbacks.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatExpectedUnitsPerCase(item) {
    const raw =
        item?.expectedUnitsPerCase ??
        item?.expected_units_per_case ??
        item?.sourceUnitsPerCaseSnapshot ??
        item?.source_units_per_case_snapshot ??
        item?.effectiveUnitsPerCase;
    if (raw === undefined || raw === null || raw === '') {
        return FC_TRANSFER_DISPLAY_FALLBACK;
    }
    return formatNumber(raw, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

/**
 * Received count/case.
 *
 * @param {Record<string, unknown>} item
 * @returns {string}
 */
export function formatReceivedUnitsPerCase(item) {
    const { unitsPerCaseRaw } = resolveReceivedDisplayFields(item);
    const raw = unitsPerCaseRaw;
    if (raw === undefined || raw === null || raw === '') {
        return FC_TRANSFER_DISPLAY_FALLBACK;
    }
    return formatNumber(raw, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}
